import { useState, useEffect, useCallback } from 'react';
import favouritesApi, { FavouriteDto } from '../api/favouritesApi';

let sharedFavourites: FavouriteDto[] = [];
let sharedLoading = false;
let hasLoaded = false;
const subscribers = new Set<() => void>();

const notifySubscribers = () => {
  subscribers.forEach((listener) => listener());
};

const setSharedFavourites = (next: FavouriteDto[]) => {
  sharedFavourites = next;
  notifySubscribers();
};

const setSharedLoading = (next: boolean) => {
  sharedLoading = next;
  notifySubscribers();
};

export const useFavourites = () => {
  const [favourites, setFavourites] = useState<FavouriteDto[]>(sharedFavourites);
  const [loading, setLoading] = useState(sharedLoading);

  useEffect(() => {
    const listener = () => {
      setFavourites(sharedFavourites);
      setLoading(sharedLoading);
    };

    subscribers.add(listener);
    listener();

    return () => {
      subscribers.delete(listener);
    };
  }, []);

  const load = useCallback(async (force = false) => {
    if (sharedLoading) return;
    if (hasLoaded && !force) return;

    setSharedLoading(true);
    try {
      const r = await favouritesApi.getFavourites();
      setSharedFavourites(r.data?.data || []);
      hasLoaded = true;
    } catch {
    } finally {
      setSharedLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const isFavourite = useCallback((type: string, reference: string) =>
    favourites.some(f => f.favouriteType === type && f.favouriteReference === reference),
    [favourites]);

  const getFavouriteId = useCallback((type: string, reference: string) =>
    favourites.find(f => f.favouriteType === type && f.favouriteReference === reference)?.favouriteId,
    [favourites]);

  const addFavourite = useCallback(async (type: string, reference: string, name: string) => {
    try {
      const r = await favouritesApi.addFavourite({ favouriteType: type, favouriteReference: reference, favouriteName: name });
      const newFav = r.data?.data;
      if (newFav) {
        setSharedFavourites([
          ...sharedFavourites.filter(f => !(f.favouriteType === type && f.favouriteReference === reference)),
          newFav,
        ]);
      }
    } catch {
    }
  }, []);

  const removeFavourite = useCallback(async (type: string, reference: string) => {
    try {
      await favouritesApi.removeFavouriteByReference(type, reference);
      setSharedFavourites(sharedFavourites.filter(f => !(f.favouriteType === type && f.favouriteReference === reference)));
    } catch {
    }
  }, []);

  const toggleFavourite = useCallback(async (type: string, reference: string, name: string) => {
    if (isFavourite(type, reference)) {
      await removeFavourite(type, reference);
    } else {
      await addFavourite(type, reference, name);
    }
  }, [isFavourite, addFavourite, removeFavourite]);

  return { favourites, loading, isFavourite, getFavouriteId, addFavourite, removeFavourite, toggleFavourite, reload: () => load(true) };
};
